import type { AtlasEdge, AtlasNode, AtlasWorkspace } from './types';

const nodeDefinitions: Omit<AtlasNode, 'locationIds'>[] = [
  {
    id: 'cap-checkin', name: 'Passenger Check-in', type: 'capability',
    description: 'Customer and airport-agent check-in capability across digital and airport channels.',
    owner: 'Airport Operations', criticality: 5, rtoMinutes: 15, impactToleranceMinutes: 45,
    estimatedRecoveryMinutes: 30, recoveryConfidence: 'documented', recoveryTier: 'Tier 1', environment: 'production',
    confidence: 'confirmed', tags: ['Airport Ops', 'Customer'], dataClassifications: [],
    sourceRefs: ['Business impact assessment'], x: 35, y: 45,
  },
  {
    id: 'app-checkin', name: 'Check-in Application', type: 'application',
    description: 'Core application used to retrieve reservations, assign seats, and issue boarding passes.',
    owner: 'Digital Operations', criticality: 5, rtoMinutes: 15, environment: 'production',
    confidence: 'confirmed', tags: ['Tier 1', 'Airport'], dataClassifications: ['Passenger PII'],
    sourceRefs: ['ServiceNow CMDB'], x: 280, y: 45,
  },
  {
    id: 'svc-identity', name: 'Identity Gateway', type: 'service',
    description: 'Shared authentication broker for workforce and operational applications.',
    owner: 'Identity & Access Management', criticality: 5, rtoMinutes: 10, environment: 'production',
    confidence: 'confirmed', tags: ['Identity', 'Shared Service'], dataClassifications: [],
    sourceRefs: ['Application architecture'], x: 525, y: 45,
  },
  {
    id: 'id-entra', name: 'Microsoft Entra ID', type: 'identity',
    description: 'Enterprise identity provider and conditional-access control plane.',
    owner: 'Identity & Access Management', criticality: 5, rtoMinutes: 15, environment: 'production',
    confidence: 'confirmed', tags: ['Identity', 'Enterprise', 'Shared Service'], dataClassifications: [],
    sourceRefs: ['Entra export', 'ServiceNow CMDB'], x: 770, y: 45,
  },
  {
    id: 'db-passenger', name: 'Passenger Database', type: 'data_store',
    description: 'Reservation and passenger records used during check-in.',
    owner: 'Data Platforms', criticality: 5, rtoMinutes: 30, environment: 'production',
    confidence: 'confirmed', tags: ['Database', 'Tier 1'], dataClassifications: ['Passenger PII'],
    sourceRefs: ['ServiceNow CMDB', 'Data catalog'], x: 280, y: 195,
  },
  {
    id: 'infra-network', name: 'Airport Network', type: 'infrastructure',
    description: 'Network path supporting airport workstations and shared application access.',
    owner: 'Network Engineering', criticality: 5, rtoMinutes: 20, environment: 'production',
    confidence: 'reported', tags: ['Network', 'Airport'], dataClassifications: [],
    sourceRefs: ['Network inventory'], x: 525, y: 195,
  },
  {
    id: 'proc-manual', name: 'Manual Check-in Procedure', type: 'procedure',
    description: 'Reduced-capacity paper and local procedure used when digital check-in is unavailable.',
    owner: 'Airport Operations', criticality: 4, rtoMinutes: 45, environment: 'other',
    confidence: 'reported', tags: ['Fallback', 'Runbook'], dataClassifications: ['Passenger PII'],
    sourceRefs: ['Continuity plan'], x: 35, y: 195,
  },
  {
    id: 'role-duty', name: 'Airport Duty Manager', type: 'role',
    description: 'On-shift role authorized to initiate manual continuity procedures.',
    owner: 'Airport Operations', criticality: 4, environment: 'other',
    confidence: 'reported', tags: ['Human dependency'], dataClassifications: [],
    sourceRefs: ['Tabletop interview'], x: 35, y: 345,
  },
  {
    id: 'vendor-weather', name: 'Weather Data Provider', type: 'vendor',
    description: 'Third-party weather observations used by airport applications.',
    owner: 'Vendor Management', criticality: 3, rtoMinutes: 120, environment: 'production',
    confidence: 'inferred', tags: ['Third party'], dataClassifications: [],
    sourceRefs: ['Contract inventory'], x: 770, y: 195,
  },
  {
    id: 'cap-flight-ops', name: 'Flight Operations Control', type: 'capability',
    description: 'Dispatch, flight planning, operational control, and disruption management from the operations control center.',
    owner: 'Integrated Operations Control', criticality: 5, rtoMinutes: 15, impactToleranceMinutes: 20,
    estimatedRecoveryMinutes: 45, recoveryConfidence: 'assumed', recoveryTier: 'Tier 0', environment: 'production',
    confidence: 'confirmed', tags: ['Flight Ops', 'Safety Critical'], dataClassifications: [],
    sourceRefs: ['Operational resilience assessment'], x: 35, y: 495,
  },
  {
    id: 'app-sabre-reservations', name: 'Sabre Reservations / PSS', type: 'application',
    description: 'Passenger service system for reservations, inventory, ticketing, and itinerary servicing.',
    owner: 'Commercial Technology', criticality: 5, rtoMinutes: 15, environment: 'production',
    confidence: 'reported', tags: ['Reservations', 'PSS', 'Tier 1'], dataClassifications: ['Passenger PII', 'Payment data'],
    sourceRefs: ['Application portfolio', 'Sabre service inventory'], x: 280, y: 345,
  },
  {
    id: 'app-flight-ops', name: 'Flight Operations System', type: 'application',
    description: 'Flight planning, dispatch release, weather, fuel, NOTAM, and operational-control workflow.',
    owner: 'Flight Operations Technology', criticality: 5, rtoMinutes: 15, environment: 'production',
    confidence: 'confirmed', tags: ['Flight Ops', 'Dispatch', 'Tier 1'], dataClassifications: [],
    sourceRefs: ['Flight operations architecture'], x: 280, y: 495,
  },
  {
    id: 'svc-acars', name: 'ACARS Messaging Service', type: 'service',
    description: 'Operational datalink used to exchange flight plans, clearances, position reports, and aircraft messages.',
    owner: 'Flight Operations Technology', criticality: 5, rtoMinutes: 10, environment: 'production',
    confidence: 'confirmed', tags: ['ACARS', 'Aircraft Datalink'], dataClassifications: [],
    sourceRefs: ['Aircraft communications architecture'], x: 525, y: 495,
  },
  {
    id: 'infra-aircraft-comms', name: 'Aircraft Communications Network', type: 'infrastructure',
    description: 'VHF, satellite, and ground-network paths carrying operational datalink messages to and from aircraft.',
    owner: 'Aircraft Communications', criticality: 5, rtoMinutes: 15, environment: 'production',
    confidence: 'reported', tags: ['VHF', 'SATCOM', 'Ground Network'], dataClassifications: [],
    sourceRefs: ['Communications provider inventory'], x: 770, y: 495,
  },
  {
    id: 'app-crew-management', name: 'Crew Management System', type: 'application',
    description: 'Crew assignments, legality, qualifications, recovery, and operational notifications.',
    owner: 'Crew Technology', criticality: 5, rtoMinutes: 30, environment: 'production',
    confidence: 'reported', tags: ['Crew', 'Operations'], dataClassifications: ['Employee PII'],
    sourceRefs: ['Application portfolio'], x: 525, y: 345,
  },
  {
    id: 'role-flight-dispatch', name: 'Licensed Flight Dispatcher', type: 'role',
    description: 'Operational-control role responsible for flight release, monitoring, and joint decisions with the captain.',
    owner: 'Flight Dispatch', criticality: 5, rtoMinutes: 0, environment: 'other',
    confidence: 'confirmed', tags: ['Human Dependency', 'Licensed Role'], dataClassifications: [],
    sourceRefs: ['Flight operations manual'], x: 770, y: 345,
  },
];

const edgeDefinitions: Omit<AtlasEdge, 'failureEffect' | 'propagationDelayMinutes' | 'evidenceIds'>[] = [
  { id: 'e1', sourceId: 'cap-checkin', targetId: 'app-checkin', type: 'DEPENDS_ON', strength: 'hard', toleranceMinutes: 0, confidence: 'confirmed' },
  { id: 'e2', sourceId: 'app-checkin', targetId: 'svc-identity', type: 'AUTHENTICATES_VIA', strength: 'hard', toleranceMinutes: 0, confidence: 'confirmed' },
  { id: 'e3', sourceId: 'svc-identity', targetId: 'id-entra', type: 'TRUSTS_IDENTITY_FROM', strength: 'hard', toleranceMinutes: 0, confidence: 'confirmed' },
  { id: 'e4', sourceId: 'app-checkin', targetId: 'db-passenger', type: 'STORES_DATA_IN', strength: 'hard', toleranceMinutes: 0, confidence: 'confirmed' },
  { id: 'e5', sourceId: 'app-checkin', targetId: 'infra-network', type: 'HOSTED_ON', strength: 'hard', toleranceMinutes: 5, confidence: 'reported' },
  { id: 'e6', sourceId: 'cap-checkin', targetId: 'proc-manual', type: 'FALLS_BACK_TO', strength: 'degraded', toleranceMinutes: 45, confidence: 'reported' },
  { id: 'e7', sourceId: 'proc-manual', targetId: 'role-duty', type: 'REQUIRES_ROLE', strength: 'hard', toleranceMinutes: 15, confidence: 'reported' },
  { id: 'e8', sourceId: 'app-checkin', targetId: 'vendor-weather', type: 'DEPENDS_ON', strength: 'optional', toleranceMinutes: 120, confidence: 'inferred' },
  { id: 'e9', sourceId: 'svc-identity', targetId: 'db-passenger', type: 'HAS_ACCESS_TO', strength: 'degraded', toleranceMinutes: 0, confidence: 'reported' },
  { id: 'e10', sourceId: 'app-checkin', targetId: 'app-sabre-reservations', type: 'CONNECTS_TO', strength: 'hard', toleranceMinutes: 0, confidence: 'reported' },
  { id: 'e11', sourceId: 'app-sabre-reservations', targetId: 'db-passenger', type: 'STORES_DATA_IN', strength: 'hard', toleranceMinutes: 0, confidence: 'reported' },
  { id: 'e12', sourceId: 'cap-flight-ops', targetId: 'app-flight-ops', type: 'DEPENDS_ON', strength: 'hard', toleranceMinutes: 0, confidence: 'confirmed' },
  { id: 'e13', sourceId: 'cap-flight-ops', targetId: 'role-flight-dispatch', type: 'REQUIRES_ROLE', strength: 'hard', toleranceMinutes: 0, confidence: 'confirmed' },
  { id: 'e14', sourceId: 'app-flight-ops', targetId: 'svc-acars', type: 'CONNECTS_TO', strength: 'hard', toleranceMinutes: 0, confidence: 'confirmed' },
  { id: 'e15', sourceId: 'svc-acars', targetId: 'infra-aircraft-comms', type: 'HOSTED_ON', strength: 'hard', toleranceMinutes: 5, confidence: 'reported' },
  { id: 'e16', sourceId: 'app-flight-ops', targetId: 'app-crew-management', type: 'CONNECTS_TO', strength: 'degraded', toleranceMinutes: 15, confidence: 'reported' },
  { id: 'e17', sourceId: 'app-flight-ops', targetId: 'app-sabre-reservations', type: 'CONNECTS_TO', strength: 'degraded', toleranceMinutes: 15, confidence: 'reported' },
  { id: 'e18', sourceId: 'app-flight-ops', targetId: 'vendor-weather', type: 'DEPENDS_ON', strength: 'degraded', toleranceMinutes: 30, confidence: 'reported' },
];

const nodes: AtlasNode[] = nodeDefinitions.map((node) => ({ ...node, locationIds: [] }));
const edges: AtlasEdge[] = edgeDefinitions.map((edge) => ({
  ...edge,
  failureEffect: edge.strength === 'hard' ? 'unavailable' : edge.strength === 'degraded' ? 'degraded' : 'none',
  propagationDelayMinutes: 0,
  evidenceIds: [],
}));

export const SAMPLE_WORKSPACE: AtlasWorkspace = {
  format: 'inventivehq.dependency-atlas',
  version: 2,
  id: 'airport-operations',
  name: 'Airline & Airport Operations',
  updatedAt: new Date().toISOString(),
  nodes,
  edges,
  scenarios: [],
  findings: [
    {
      id: 'finding-1', category: 'graph', title: 'Missing fallback relationship',
      description: 'Confirm whether remote stations can use locally cached passenger manifests during an identity outage.',
      priority: 'high', owner: 'Airport Operations', status: 'open', linkedNodeId: 'cap-checkin',
    },
  ],
  tabletop: {
    title: 'Enterprise Identity and Flight Operations Disruption',
    objective: 'Validate passenger processing, flight dispatch, aircraft communications, crew coordination, and manual continuity during a shared-service outage.',
    participants: ['Integrated Ops Control', 'Flight Dispatch', 'Airport Ops', 'IAM', 'Crew Operations'],
    observations: '',
    currentInject: 'New staff sessions cannot authenticate. Airport agents are losing departure-control access while dispatchers report delayed ACARS acknowledgements.',
  },
  fallbackProfiles: [{
    id: 'fallback-passenger-checkin', capabilityId: 'cap-checkin', procedureId: 'proc-manual',
    activationMinutes: 15, throughputPercent: 30, maximumDurationMinutes: 180,
    requiredRoles: ['Airport duty manager', 'Station agents'], requiredResources: ['Printed forms', 'Locally cached passenger manifest'],
  }],
};
